import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import type {
	SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto,
	SmartField_Api_Contracts_Mapping_ChamberMappingDto,
	SmartField_Api_Contracts_Mapping_ChamberMappingUpdateDto,
	SmartField_Api_Contracts_Sensor_DimSensorDto,
	SmartField_Api_Contracts_Sensor_DimSensorPropertiesUpdateDto,
} from "../../api";
import { HeightHistoryChart } from "./HeightHistoryChart";
import { PlacementHistoryTimeline } from "./PlacementHistoryTimeline";
import { usePlacementHistoryForSensors } from "./usePlacementHistory";
import { nowDatetimeLocal, datetimeLocalToUtcIso } from "../../utils/dateUtils";

type Props = {
	mapping: SmartField_Api_Contracts_Mapping_ChamberMappingDto;
	els: SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto[];
	sensors: SmartField_Api_Contracts_Sensor_DimSensorDto[];
	onSaveMapping: (data: SmartField_Api_Contracts_Mapping_ChamberMappingUpdateDto) => void;
	onSaveHeights: (
		elId: number,
		effectiveFrom: string,
		effectiveTo: string | null,
		data: SmartField_Api_Contracts_Sensor_DimSensorPropertiesUpdateDto,
	) => Promise<void>;
	onDelete: () => void;
	onCancel: () => void;
	isSaving: boolean;
	isDeleting: boolean;
	canEdit: boolean;
};

export function ChamberConfigModal({
	mapping,
	els,
	sensors,
	onSaveMapping,
	onSaveHeights,
	onDelete,
	onCancel,
	isSaving,
	isDeleting,
	canEdit,
}: Props) {
	const initialPlotEl = els.find(
		(el) => el.state === "Plot" && el.fieldId === mapping.trialId && el.plotId === mapping.plotNumber,
	);

	const [fieldId, setFieldId] = useState<string>(
		initialPlotEl?.fieldId != null ? String(initialPlotEl.fieldId) : "",
	);
	const [plotElId, setPlotElId] = useState<string>(
		initialPlotEl?.id != null ? String(initialPlotEl.id) : "",
	);
	const [trialId, setTrialId] = useState(String(mapping.trialId ?? ""));

	const fields = useMemo(
		() =>
			[...els]
				.filter((el) => el.state === "Field")
				.sort((a, b) => (a.fieldId ?? 0) - (b.fieldId ?? 0)),
		[els],
	);
	const plotsInField = useMemo(
		() =>
			[...els]
				.filter(
					(el) =>
						el.state === "Plot" &&
						(fieldId === "" || el.fieldId === Number(fieldId)),
				)
				.sort((a, b) => (a.plotId ?? 0) - (b.plotId ?? 0)),
		[els, fieldId],
	);

	const plotEl = els.find((el) => String(el.id) === plotElId);

	const affectedSensors = useMemo(
		() =>
			sensors.filter(
				(s) =>
					s.currentExperimentalLocationId === plotEl?.id &&
					s.manufacturer === "Aeris",
			),
		[sensors, plotEl],
	);

	const [hasExtension, setHasExtension] = useState(
		localStorage.getItem("chamber-last-has-extension") !== "false",
	);
	const [extension, setExtension] = useState(
		localStorage.getItem("chamber-last-extension") ?? "0.5",
	);
	const [chamberHeight, setChamberHeight] = useState(
		localStorage.getItem("chamber-last-height") ?? "0.25",
	);
	const [extensionIsOpaque, setExtensionIsOpaque] = useState(
		localStorage.getItem("chamber-last-extension-opaque") === "true",
	);

	useEffect(() => {
		setHasExtension(localStorage.getItem("chamber-last-has-extension") !== "false");
		setExtension(localStorage.getItem("chamber-last-extension") ?? "0.5");
		setChamberHeight(localStorage.getItem("chamber-last-height") ?? "0.25");
		setExtensionIsOpaque(localStorage.getItem("chamber-last-extension-opaque") === "true");
	}, [plotElId]);
	const [effectiveFrom, setEffectiveFrom] = useState(
		localStorage.getItem("chamber-last-eff-from") ?? nowDatetimeLocal(),
	);
	const [effectiveTo, setEffectiveTo] = useState(localStorage.getItem("chamber-last-eff-to") ?? "");
	const [confirmedOverwrite, setConfirmedOverwrite] = useState(false);

	const diverges = useMemo(() => {
		if (affectedSensors.length < 2) {
			return false;
		}
		const distinct = <T,>(values: (T | null | undefined)[]) =>
			new Set(values.map((v) => v ?? null)).size > 1;
		return (
			distinct(affectedSensors.map((s) => s.chamberExtensionInMeters)) ||
			distinct(affectedSensors.map((s) => s.chamberHeightInMeters)) ||
			distinct(affectedSensors.map((s) => s.chamberExtensionIsOpaque))
		);
	}, [affectedSensors]);

	const { entries: historyEntries, isLoading: historyLoading, isError: historyError } =
		usePlacementHistoryForSensors(affectedSensors);

	const handleSaveMapping = () => {
		const trial = parseInt(trialId, 10);
		if (!plotEl?.plotId || Number.isNaN(trial)) {
			toast.error("Select a plot and enter a valid trial ID");
			return;
		}
		onSaveMapping({ year: mapping.year, chamberNumber: mapping.chamberNumber, trialId: trial, plotNumber: plotEl.plotId });
	};

	const handleSaveHeights = async () => {
		if (!plotEl) {
			toast.error("Select a plot before saving heights");
			return;
		}
		if (affectedSensors.length === 0) {
			toast.error("No sensors are affected by this plot — nothing to save");
			return;
		}
		if (!chamberHeight) {
			toast.error("Chamber height is required");
			return;
		}
		const chamberHeightValue = parseFloat(chamberHeight);
		if (!Number.isFinite(chamberHeightValue) || chamberHeightValue <= 0) {
			toast.error("Chamber height must be a positive number");
			return;
		}
		let extensionValue: number | null = null;
		if (hasExtension) {
			extensionValue = parseFloat(extension);
			if (!Number.isFinite(extensionValue) || extensionValue <= 0) {
				toast.error("Extension height must be a positive number");
				return;
			}
		}
		if (diverges && !confirmedOverwrite) {
			toast.error("Confirm overwrite of diverging sensor values before saving");
			return;
		}
		try {
			await onSaveHeights(plotEl.id!, datetimeLocalToUtcIso(effectiveFrom), effectiveTo ? datetimeLocalToUtcIso(effectiveTo) : null, {
				towerHeightInMeters: null,
				chamberHeightInMeters: chamberHeightValue,
				chamberExtensionInMeters: extensionValue,
				chamberExtensionIsOpaque: hasExtension ? extensionIsOpaque : false,
			});
			localStorage.setItem("chamber-last-has-extension", String(hasExtension));
			localStorage.setItem("chamber-last-extension", extension);
			localStorage.setItem("chamber-last-height", chamberHeight);
			localStorage.setItem("chamber-last-extension-opaque", String(extensionIsOpaque));
			localStorage.setItem("chamber-last-eff-from", effectiveFrom);
			localStorage.setItem("chamber-last-eff-to", effectiveTo);
		} catch (err) {
			toast.error(err instanceof Error ? err.message : "Failed to save chamber heights");
		}
	};

	return (
		<div className="p-5 space-y-5">
			<h3 className="text-sm font-semibold text-gray-900">
				Configure Chamber {mapping.chamberNumber}
			</h3>

			<div className="space-y-3">
				<h4 className="text-xs font-semibold text-gray-500 uppercase">Mapping</h4>
				<div>
					<label htmlFor="chamber-field" className="block text-xs font-medium text-gray-700 mb-1">Field</label>
					<select
						id="chamber-field"
						value={fieldId}
						onChange={(e) => {
							setFieldId(e.target.value);
							setPlotElId("");
						}}
						disabled={!canEdit}
						className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-smartfield bg-white text-gray-900 disabled:bg-gray-50"
					>
						<option value="">Select field…</option>
						{fields.map((f) => (
							<option key={f.id} value={String(f.fieldId)}>
								{f.friendlyName ?? f.entityIdentifier ?? `Field ${f.fieldId}`}
							</option>
						))}
					</select>
				</div>
				<div>
					<label htmlFor="chamber-plot" className="block text-xs font-medium text-gray-700 mb-1">Plot</label>
					<select
						id="chamber-plot"
						value={plotElId}
						onChange={(e) => setPlotElId(e.target.value)}
						disabled={!canEdit}
						className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-smartfield bg-white text-gray-900 disabled:bg-gray-50"
					>
						<option value="">Select plot…</option>
						{plotsInField.map((p) => (
							<option key={p.id} value={String(p.id)}>
								{p.friendlyName ?? `Plot ${p.plotId}`}
							</option>
						))}
					</select>
				</div>
				<div>
					<label htmlFor="chamber-trial" className="block text-xs font-medium text-gray-700 mb-1">Trial ID</label>
					<input
						id="chamber-trial"
						type="number"
						value={trialId}
						onChange={(e) => setTrialId(e.target.value)}
						disabled={!canEdit}
						className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-smartfield bg-white text-gray-900 disabled:bg-gray-50"
					/>
				</div>
				{canEdit && (
					<div className="flex gap-2">
						<button
							type="button"
							onClick={handleSaveMapping}
							disabled={isSaving}
							className="px-3 py-1.5 text-sm bg-smartfield text-white rounded hover:bg-smartfield/90 disabled:opacity-50"
						>
							{isSaving ? "Saving…" : "Save Mapping"}
						</button>
						<button
							type="button"
							onClick={onDelete}
							disabled={isDeleting}
							className="px-3 py-1.5 text-sm bg-red-50 text-red-600 border border-red-200 rounded hover:bg-red-100 disabled:opacity-50"
						>
							{isDeleting ? "…" : "Delete"}
						</button>
					</div>
				)}
			</div>

			<div className="space-y-3 border-t border-gray-100 pt-4">
				<h4 className="text-xs font-semibold text-gray-500 uppercase">Heights</h4>

				<p className="text-xs text-gray-500">
					Affected sensors:{" "}
					{affectedSensors.length === 0
						? "none"
						: Array.from(
								new Map(
									affectedSensors.map((s) => [
										s.deviceSerialKey,
										s.deviceFriendlyName ?? s.deviceSerialKey ?? `Channel ${s.channelNumber}`,
									]),
								).values(),
							).join(", ")}
				</p>

				{diverges && (
					<div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 space-y-1.5">
						<p>Affected sensors currently have diverging height values. Saving will overwrite all of them with the values below.</p>
						<label className="flex items-center gap-1.5">
							<input
								type="checkbox"
								checked={confirmedOverwrite}
								onChange={(e) => setConfirmedOverwrite(e.target.checked)}
							/>
							I understand this will overwrite diverging values
						</label>
					</div>
				)}

				<div>
					<label htmlFor="chamber-height" className="block text-xs font-medium text-gray-700 mb-1">Chamber incl. frame (m) *</label>
					<input
						id="chamber-height"
						type="number"
						step="0.01"
						value={chamberHeight}
						onChange={(e) => setChamberHeight(e.target.value)}
						disabled={!canEdit}
						className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-smartfield bg-white text-gray-900 disabled:bg-gray-50"
					/>
				</div>
				<label className="flex items-center gap-1.5 text-xs font-medium text-gray-700">
					<input
						type="checkbox"
						checked={hasExtension}
						onChange={(e) => setHasExtension(e.target.checked)}
						disabled={!canEdit}
					/>
					Has extension
				</label>
				{hasExtension && (
					<>
						<div>
							<label htmlFor="chamber-extension" className="block text-xs font-medium text-gray-700 mb-1">Extension (m)</label>
							<input
								id="chamber-extension"
								type="number"
								step="0.01"
								value={extension}
								onChange={(e) => setExtension(e.target.value)}
								disabled={!canEdit}
								className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-smartfield bg-white text-gray-900 disabled:bg-gray-50"
							/>
						</div>
						<label className="flex items-center gap-1.5 text-xs font-medium text-gray-700">
							<input
								type="checkbox"
								checked={extensionIsOpaque}
								onChange={(e) => setExtensionIsOpaque(e.target.checked)}
								disabled={!canEdit}
							/>
							Extension is opaque
						</label>
					</>
				)}
				<div className="grid grid-cols-2 gap-2">
					<div>
						<label htmlFor="chamber-eff-from" className="block text-xs font-medium text-gray-700 mb-1">Effective from</label>
						<input
							id="chamber-eff-from"
							type="datetime-local"
							value={effectiveFrom}
							onChange={(e) => setEffectiveFrom(e.target.value)}
							disabled={!canEdit}
							className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-smartfield bg-white text-gray-900 disabled:bg-gray-50"
						/>
					</div>
					<div>
						<label htmlFor="chamber-eff-to" className="block text-xs font-medium text-gray-700 mb-1">Effective to</label>
						<input
							id="chamber-eff-to"
							type="datetime-local"
							value={effectiveTo}
							onChange={(e) => setEffectiveTo(e.target.value)}
							disabled={!canEdit}
							className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-smartfield bg-white text-gray-900 disabled:bg-gray-50"
						/>
					</div>
				</div>
				{canEdit && (
					<>
					<p className="text-xs text-gray-400">Values above are prefilled from your last edit — review and click Save to apply.</p>
					<button
						type="button"
						onClick={handleSaveHeights}
						disabled={isSaving || !chamberHeight || affectedSensors.length === 0}
						className="px-3 py-1.5 text-sm bg-smartfield text-white rounded hover:bg-smartfield/90 disabled:opacity-50"
					>
						{isSaving ? "Saving…" : "Save Heights"}
					</button>
					</>
				)}

				{affectedSensors.length > 0 && (
					<>
						{!historyLoading && !historyError && (
							<HeightHistoryChart entries={historyEntries} field="chamber" />
						)}
						<PlacementHistoryTimeline
							entries={historyEntries}
							els={els}
							field="chamber"
							isLoading={historyLoading}
							isError={historyError}
						/>
					</>
				)}
			</div>

			<div className="pt-2">
				<button
					type="button"
					onClick={onCancel}
					className="px-3 py-1.5 text-sm border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
				>
					Close
				</button>
			</div>
		</div>
	);
}
